import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { API } from '@/App';
import { Loader2, MapPin, Plane, ExternalLink } from 'lucide-react';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';

// Dynamic import for Globe
let Globe = null;

export default function GlobeView() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPost, setSelectedPost] = useState(null);
  const [globeReady, setGlobeReady] = useState(false);
  const globeEl = useRef();

  useEffect(() => {
    // Dynamic import of react-globe.gl
    import('react-globe.gl').then((mod) => {
      Globe = mod.default;
      setGlobeReady(true);
    }).catch(err => {
      console.error('Failed to load globe:', err);
    });

    fetchMapData();
  }, []);

  const fetchMapData = async () => {
    try {
      const response = await axios.get(`${API}/map-data`);
      setPosts(response.data);
    } catch (error) {
      console.error('Failed to fetch map data:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (globeEl.current && globeReady) {
      // Auto-rotate
      globeEl.current.controls().autoRotate = true;
      globeEl.current.controls().autoRotateSpeed = 0.5;
      
      // Set initial view
      globeEl.current.pointOfView({ lat: 30, lng: 0, altitude: 2.5 });
    }
  }, [globeReady, posts]);

  const handlePointClick = (point) => {
    setSelectedPost(point);
    if (globeEl.current) {
      globeEl.current.pointOfView({
        lat: point.latitude,
        lng: point.longitude,
        altitude: 1.5
      }, 1000);
    }
  };

  if (loading || !globeReady) {
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="globe-loading">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-purple-500 mx-auto mb-4" />
          <p className="text-gray-400">Loading globe...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" data-testid="globe-page">
      {/* Header */}
      <div className="text-center pt-8 pb-4 px-4">
        <h1 
          className="text-3xl sm:text-4xl font-bold text-white mb-2"
          style={{ fontFamily: "'Space Grotesk', sans-serif" }}
        >
          Window Views Worldwide
        </h1>
        <p className="text-gray-400">
          {posts.length} views captured from around the globe
        </p>
      </div>

      {/* Globe Container */}
      <div className="relative max-w-6xl mx-auto px-4">
        <div className="globe-container">
          {Globe && (
            <Globe
              ref={globeEl}
              globeImageUrl="//unpkg.com/three-globe/example/img/earth-dark.jpg"
              bumpImageUrl="//unpkg.com/three-globe/example/img/earth-topology.png"
              backgroundImageUrl=""
              backgroundColor="rgba(0,0,0,0)"
              atmosphereColor="#9D4EDD"
              atmosphereAltitude={0.15}
              pointsData={posts}
              pointLat="latitude"
              pointLng="longitude"
              pointColor={() => '#F72585'}
              pointAltitude={0.01}
              pointRadius={0.5}
              pointsMerge={false}
              onPointClick={handlePointClick}
              labelsData={posts}
              labelLat="latitude"
              labelLng="longitude"
              labelText={(d) => ''}
              labelSize={0}
              labelDotRadius={0.3}
              labelColor={() => 'rgba(157, 78, 221, 0.8)'}
              width={typeof window !== 'undefined' ? Math.min(window.innerWidth - 32, 1152) : 800}
              height={600}
            />
          )}
        </div>

        {/* Selected Post Panel */}
        {selectedPost && (
          <div 
            className="absolute bottom-4 left-4 right-4 md:left-auto md:right-4 md:w-80 glass-card rounded-2xl p-4 animate-fade-in-up"
            data-testid="selected-post-panel"
          >
            <button 
              className="absolute top-2 right-2 text-gray-400 hover:text-white"
              onClick={() => setSelectedPost(null)}
            >
              ×
            </button>
            
            <div className="flex gap-3">
              <div className="w-20 h-20 rounded-lg overflow-hidden flex-shrink-0">
                <img 
                  src={selectedPost.image_url} 
                  alt="Window view"
                  className="w-full h-full object-cover"
                />
              </div>
              
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <Avatar className="w-5 h-5">
                    <AvatarImage src={selectedPost.author?.picture} />
                    <AvatarFallback className="bg-purple-900 text-white text-xs">
                      {selectedPost.author?.name?.charAt(0)}
                    </AvatarFallback>
                  </Avatar>
                  <span className="text-sm text-gray-300 truncate">
                    {selectedPost.author?.name}
                  </span>
                </div>
                
                {selectedPost.caption && (
                  <p className="text-sm text-white line-clamp-2 mb-2">
                    {selectedPost.caption}
                  </p>
                )}

                <div className="flex flex-wrap gap-1">
                  {selectedPost.airline && (
                    <span className="flight-tag text-xs">
                      <Plane className="w-2 h-2" />
                      {selectedPost.airline}
                    </span>
                  )}
                  {selectedPost.departure && selectedPost.arrival && (
                    <span className="flight-tag text-xs">
                      {selectedPost.departure} → {selectedPost.arrival}
                    </span>
                  )}
                </div>
              </div>
            </div>

            <Link 
              to={`/post/${selectedPost.post_id}`}
              className="mt-3 flex items-center justify-center gap-2 w-full py-2 rounded-full bg-purple-500/20 text-purple-400 text-sm hover:bg-purple-500/30 transition-colors"
            >
              View Full Post
              <ExternalLink className="w-3 h-3" />
            </Link>
          </div>
        )}
      </div>

      {/* Instructions */}
      <div className="text-center py-8 px-4">
        <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/5 border border-white/10">
          <MapPin className="w-4 h-4 text-purple-400" />
          <span className="text-sm text-gray-400">
            Click on any point to see the window view from that location
          </span>
        </div>
      </div>

      {posts.length === 0 && (
        <div className="text-center py-12">
          <p className="text-gray-400">
            No geotagged posts yet. Be the first to add a location to your window view!
          </p>
        </div>
      )}
    </div>
  );
}

export { GlobeView };
